import * as LearningModuleActions from "src/app/store/learning-module/actions";
import * as FromLearningModule from "src/app/store/learning-module/selectors";
import * as FromRouter from "src/app/store/router/selectors";
import { ChangeDetectionStrategy, Component, ElementRef, ViewChild } from "@angular/core";
import { IonRouterOutlet, NavController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { AppRoutes } from "src/app/constants";
import { RootState } from "src/app/store";
import { first, mergeMap, skipWhile } from "rxjs/operators";
import { combineLatest, Observable } from "rxjs";
import { ChallengeDto, ChapterDto, StepAttachmentDto, LearningModuleDto, StepDto } from "@api";
import { NavigationService } from "src/app/services/navigation/navigation.service";
import { Browser } from "@capacitor/browser";

@Component({
  selector: "app-step",
  templateUrl: "./step.page.html",
  styleUrls: ["./step.page.scss", "../challenge/challenge.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepPage {
  @ViewChild("video") private readonly video: ElementRef<HTMLVideoElement>;

  public readonly routes: typeof AppRoutes = AppRoutes;
  public readonly moduleId$: Observable<string> = this.store.select(FromRouter.selectModuleId);
  public readonly chapterId$: Observable<string> = this.store.select(FromRouter.selectChapterId);
  public readonly challengeId$: Observable<string> = this.store.select(FromRouter.selectChallengeId);
  public readonly stepId$: Observable<string> = this.store.select(FromRouter.selectStepId);
  public readonly learningModule$: Observable<LearningModuleDto> = this.moduleId$.pipe(
    mergeMap(moduleId => this.store.select(FromLearningModule.selectLearningModule(moduleId))),
  );
  public readonly chapter$: Observable<ChapterDto> = this.chapterId$.pipe(
    mergeMap(chapterId => this.store.select(FromLearningModule.selectChapter(chapterId))),
  );
  public readonly challenge$: Observable<ChallengeDto> = this.challengeId$.pipe(
    mergeMap(challengeId => this.store.select(FromLearningModule.selectChallenge(challengeId))),
  );
  public readonly steps$: Observable<StepDto[]> = this.challengeId$.pipe(
    mergeMap(challengeId => this.store.select(FromLearningModule.selectChallengeSteps(challengeId))),
  );
  public readonly step$: Observable<StepDto> = this.stepId$.pipe(
    mergeMap(stepId => this.store.select(FromLearningModule.selectStep(stepId))),
  );
  public breadcrumbs: any[] = [];
  public defaultHref: string;
  public defaultImage: string;

  private timeout: any;

  constructor(
    private readonly store: Store<RootState>,
    private readonly i18n: TranslateService,
    private readonly routerOutlet: IonRouterOutlet,
    private readonly navController: NavController,
    private readonly navigationService: NavigationService,
  ) {}

  public stepChanged(step: StepDto): void {
    if (this.timeout) clearTimeout(this.timeout);

    this.video?.nativeElement.pause();
    combineLatest([this.learningModule$, this.chapter$, this.challenge$])
      .pipe(first())
      .subscribe(([learningModule, chapter, challenge]) => {
        this.navController.navigateForward(
          [
            AppRoutes.learningModules,
            learningModule.id,
            AppRoutes.chapters,
            chapter.id,
            AppRoutes.challenges,
            challenge.id,
            AppRoutes.steps,
            step.id,
          ],
          { animated: false },
        );
      });
  }

  public openFile(attachment: StepAttachmentDto): void {
    Browser.open({ url: attachment.url, windowName: attachment.name, presentationStyle: "popover" });
  }

  public next(): void {
    combineLatest([this.moduleId$, this.chapterId$, this.challengeId$, this.stepId$, this.steps$])
      .pipe(first())
      .subscribe(([learningModuleId, chapterId, challengeId, stepId, steps]) => {
        this.store.dispatch(LearningModuleActions.markStepAsCompleted({ learningModuleId, chapterId, challengeId, stepId }));

        const currentIndex = steps.findIndex(step => step.id === stepId);
        if (currentIndex !== -1) this.stepChanged({ id: steps[currentIndex + 1]?.id } as StepDto);
      });
  }

  public finishChallenge(): void {
    combineLatest([this.moduleId$, this.chapterId$, this.challengeId$, this.stepId$])
      .pipe(first())
      .subscribe(([learningModuleId, chapterId, challengeId, stepId]) => {
        this.store.dispatch(LearningModuleActions.markStepAsCompleted({ learningModuleId, chapterId, challengeId, stepId }));
        this.navController.navigateBack([AppRoutes.learningModules, learningModuleId, AppRoutes.chapters, chapterId]);
      });
  }

  public finishChapter(): void {
    combineLatest([this.moduleId$, this.chapterId$, this.challengeId$, this.stepId$])
      .pipe(first())
      .subscribe(([learningModuleId, chapterId, challengeId, stepId]) => {
        this.store.dispatch(LearningModuleActions.markStepAsCompleted({ learningModuleId, chapterId, challengeId, stepId }));
        this.navController.navigateBack([AppRoutes.learningModules, learningModuleId]);
      });
  }

  public openSurvey(): void {
    this.challenge$.pipe(first()).subscribe(challenge => {
      this.navigationService.handleNavigationTo(`/${AppRoutes.surveys}/${challenge.surveyId}`, this.routerOutlet.nativeEl);
    });
  }

  protected ionViewWillEnter(): void {
    combineLatest([this.learningModule$, this.chapter$, this.challenge$])
      .pipe(
        skipWhile(([learningModule, chapter, challenge]) => !learningModule || !challenge || !chapter),
        first(),
      )
      .subscribe(([learningModule, chapter, challenge]) => {
        this.breadcrumbs = [
          { name: this.i18n.instant("DASHBOARD.BREADCRUMB_NAME"), link: `/${this.routes.home}` },
          { name: this.i18n.instant("LEARNING_MODULES.BREADCRUMB_NAME"), link: `/${AppRoutes.learningModules}` },
          { name: learningModule.name, link: `/${AppRoutes.learningModules}/${learningModule.id}` },
          {
            name: chapter.name,
            link: `/${AppRoutes.learningModules}/${learningModule.id}/${AppRoutes.chapters}/${chapter.id}`,
          },
          {
            name: challenge.name,
            link: `/${AppRoutes.learningModules}/${learningModule.id}/${AppRoutes.chapters}/${chapter.id}/${AppRoutes.challenges}/${challenge.id}`,
          },
        ];
        this.defaultHref = `/${AppRoutes.learningModules}/${learningModule.id}/${AppRoutes.chapters}/${chapter.id}/${AppRoutes.challenges}/${challenge.id}`;
        this.defaultImage = `https://via.placeholder.com/50/${learningModule.color?.slice(1)}?text=%20`;
      });

    combineLatest([this.steps$, this.stepId$])
      .pipe(
        skipWhile(([steps]) => !steps.length),
        first(),
      )
      .subscribe(([steps, stepId]) => {
        if (!stepId) this.stepChanged({ id: steps[0].id } as StepDto);
      });

    combineLatest([this.moduleId$, this.chapterId$, this.challengeId$, this.step$])
      .pipe(
        skipWhile(([, , , step]) => !step),
        first(),
      )
      .subscribe(([learningModuleId, chapterId, challengeId, { id, markAsViewedAt }]) => {
        this.timeout = setTimeout(
          () => this.store.dispatch(LearningModuleActions.markStepAsCompleted({ learningModuleId, chapterId, challengeId, stepId: id })),
          markAsViewedAt,
        );
      });
  }

  protected ionViewDidEnter(): void {
    combineLatest([this.moduleId$, this.chapterId$, this.challengeId$, this.stepId$])
      .pipe(first())
      .subscribe(([learningModuleId, chapterId, challengeId, stepId]) => {
        this.store.dispatch(LearningModuleActions.getLearningModule({ learningModuleId }));
        this.store.dispatch(LearningModuleActions.getChapter({ learningModuleId, chapterId }));
        this.store.dispatch(LearningModuleActions.getChapterChallenges({ learningModuleId, chapterId }));
        this.store.dispatch(LearningModuleActions.getChallenge({ learningModuleId, challengeId, chapterId }));
        this.store.dispatch(LearningModuleActions.getChallengeSteps({ learningModuleId, challengeId, chapterId }));
      });
  }
}
